import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SERVICE_MODE } from '@/constants/plan';
import {
  initialState,
  loginUserInfoSlice,
  LoginUserInfoState,
} from '@/states/slices/loginUserInfoSlice';
import { AppDispatch, RootState, useAppDispatch } from '@/states/store';

import { PlanInfo as ApiPlanInfo } from '../features/api/planInfoApi';

/**
 * ログイン中のユーザ情報を管理するためのカスタムフック
 */
export const useLoginUserInfo = () => {
  const dispatch: AppDispatch = useAppDispatch();

  const { setLoginUserInfoFromApi, setLoginUserInfo, clearLoginUserInfo } =
    loginUserInfoSlice.actions;

  const { planInfo } = useSelector<RootState, LoginUserInfoState>(
    (state) => state.loginUserInfo,
  );

  /**
   * APIレスポンスからプラン情報を更新
   */
  const changeLoginUserInfoFromApi = useCallback(
    (value: ApiPlanInfo | undefined) => {
      dispatch(setLoginUserInfoFromApi(value));
    },
    [dispatch, setLoginUserInfoFromApi],
  );

  /**
   * 変換済みのプラン情報を更新
   */
  const changeLoginUserInfo = useCallback(
    (value: LoginUserInfoState) => {
      dispatch(setLoginUserInfo(value));
    },
    [dispatch, setLoginUserInfo],
  );

  /**
   * プラン情報を取得済かチェック
   */
  const isPlanInfoFetched = useMemo(
    () => JSON.stringify(planInfo) !== JSON.stringify(initialState.planInfo),
    [planInfo],
  );

  /**
   * 全てのStateをリセット
   */
  const resetState = useCallback(() => {
    dispatch(clearLoginUserInfo());
  }, [dispatch, clearLoginUserInfo]);

  /**
   * お試しプランでログイン中か否か
   */
  const isFreePlan = useMemo(() => planInfo.isTrial, [planInfo.isTrial]);

  /**
   * 旧プランでログイン中か否か
   * 利用サービスがliveの場合ライブ通訳利用ユーザ
   */
  const isSerialPlan = useMemo(
    () => planInfo.service === SERVICE_MODE.LIVE,
    [planInfo.service],
  );

  /**
   * Sentioプランでログイン中か否か
   * 利用サービスがSentioの場合Sentio利用ユーザ
   */
  const isSentioPlan = useMemo(
    () => planInfo.service === SERVICE_MODE.SENTIO,
    [planInfo.service],
  );

  /**
   * 共有機能が利用可能か否か
   */
  const isShareable = useMemo(
    () => planInfo.stt.view.enabled,
    [planInfo.stt.view.enabled],
  );

  /**
   * 一時停止機能を利用可能か否か
   */
  const isPausable = useMemo(
    () => planInfo.stt.view.pausable,
    [planInfo.stt.view.pausable],
  );

  /**
   * STT連続使用時間が制限されているか否か
   */
  const isSttContinueTimeLimit = useMemo(
    () => planInfo.stt.continueMin >= 0,
    [planInfo.stt.continueMin],
  );

  /**
   * STT連続使用時間(単位：分)を返却
   */
  const sttContinueTime = useMemo(
    () => planInfo.stt.continueMin,
    [planInfo.stt.continueMin],
  );

  /**
   * 共有機能利用可能時間が制限されているか否か
   */
  const isShareTimeLimit = useMemo(
    () => planInfo.stt.view.availableMin >= 0,
    [planInfo.stt.view.availableMin],
  );

  /**
   * 共有機能利用可能時間(単位：分)を返却
   */
  const shareTime = useMemo(
    () => planInfo.stt.view.availableMin,
    [planInfo.stt.view.availableMin],
  );

  /**
   * フッターを固定表示するか否か
   */
  const isFooterLock = useMemo(
    () => planInfo.stt.footerLock,
    [planInfo.stt.footerLock],
  );

  /**
   * STT無音タイムアウト時間(単位：分)を返却
   */
  const sttSilenceTimeoutMin = useMemo(
    () => planInfo.stt.silenceTimeoutMin,
    [planInfo.stt.silenceTimeoutMin],
  );

  /**
   * マニュアルの遷移先を返却
   */
  const manualUrl = useMemo(() => planInfo.manualUrls, [planInfo.manualUrls]);

  return {
    isFreePlan,
    isSerialPlan,
    isSentioPlan,
    isShareable,
    isPausable,
    isSttContinueTimeLimit,
    sttContinueTime,
    isShareTimeLimit,
    shareTime,
    manualUrl,
    setPlanInfoFromApi: changeLoginUserInfoFromApi,
    setPlanInfo: changeLoginUserInfo,
    resetState,
    isPlanInfoFetched,
    isFooterLock,
    sttSilenceTimeoutMin,
  };
};
